import { Controller } from "@hotwired/stimulus";

// Drives the affiliate invite modal. Two ways to add an invitee:
//   1. Member typeahead — type into the search field, the results frame reloads
//      via its `src` (server renders `search_affiliates`), pick a result to add.
//   2. Raw email — the "invite" button / Enter adds whatever's typed as an email,
//      for people who aren't community members yet (no user_id).
//
// Each added invitee becomes an <li> with the two hidden inputs the InvitesForm
// expects (`invites[][user_id]`, `invites[][email]`). Server-rendered rows (the
// buffered draft) and cloned rows share the same shape, so the form survives a
// validation-error re-render. Already-added people are hidden from the results
// so they can't be added twice; removing a row frees them up again.
export default class extends Controller {
  static targets = [
    "list",
    "searchInput",
    "results",
    "template",
    "templateEmail",
    "templateInitials",
    "templateLabel",
    "templateSubline",
    "item",
    "result",
    "empty"
  ];
  static values = {
    searchUrl: String
  };

  connect() {
    this.addedUserIds = new Set();
    this.addedEmails = new Set();

    // Seed the dedup sets from rows already rendered by the server (the buffered draft).
    this.itemTargets.forEach((item) => this.trackRow(item));
    this.notifyChanged();

    // Tapping a result must not blur the search input: the blur closes the
    // keyboard and resizes the sheet under the finger before the click lands.
    // Combobox pattern — mousedown only fires for taps (after scroll gestures
    // are ruled out), so scrolling the result list stays untouched. The
    // listener sits on the turbo-frame, which survives result reloads.
    this.onResultsMousedown = (event) => {
      if (event.target.closest("button")) event.preventDefault();
    };
    if (this.hasResultsTarget) {
      this.resultsTarget.addEventListener("mousedown", this.onResultsMousedown);
    }
  }

  disconnect() {
    if (this.hasResultsTarget) {
      this.resultsTarget.removeEventListener("mousedown", this.onResultsMousedown);
    }
  }

  // Keeps the search input focused when the invite button is tapped — same
  // rationale as onResultsMousedown in connect().
  preventBlur(event) {
    event.preventDefault();
  }

  // input on the search field -> debounced reload of the results frame.
  search() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.performSearch(), 200);
  }

  performSearch() {
    const query = this.searchInputTarget.value.trim();

    if (query.length < 2) {
      this.clearResults();
      return;
    }

    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);
    this.resultsTarget.src = url.pathname + url.search;
  }

  // click on a member result -> add that member (has a user_id).
  add(event) {
    const result = event.currentTarget.closest(
      `[data-${this.identifier}-target="result"]`
    );
    if (!result) return;

    const userId = result.dataset.userId;
    const email = result.dataset.email;
    const label =
      result.querySelector(".affiliate-invite-row__text .label--large")?.textContent?.trim() ||
      email;

    this.appendRow({ userId, email, label });
    this.clearSearch();
  }

  // "invite" button / Enter -> add the typed value as a raw email (no user_id).
  addEmail(event) {
    event.preventDefault();

    const email = this.searchInputTarget.value.trim();
    if (!email || !this.isValidEmail(email)) return;

    if (this.addedEmails.has(email)) {
      this.clearSearch();
      return;
    }

    this.appendRow({ userId: "", email, label: email });
    this.clearSearch();
  }

  // remove button on any row -> drop it and free the person up for re-adding.
  remove(event) {
    const row = event.currentTarget.closest(".affiliate-invite-row");
    if (!row) return;

    const email = row.querySelector("input[name*='[email]']")?.value;
    const userId = row.querySelector("input[name*='[user_id]']")?.value;
    if (email) this.addedEmails.delete(email);
    if (userId) this.addedUserIds.delete(userId);

    row.remove();
    this.notifyChanged();
  }

  // Fired as each result <li> enters the DOM after a frame load. Hide the ones
  // already buffered this session so they can't be added twice.
  resultTargetConnected(result) {
    if (
      this.addedUserIds.has(result.dataset.userId) ||
      this.addedEmails.has(result.dataset.email)
    ) {
      result.hidden = true;
    }
  }

  appendRow({ userId, email, label }) {
    const fragment = this.templateTarget.content.cloneNode(true);
    const item = fragment.querySelector("li");

    item.querySelector("input[name*='[user_id]']").value = userId;
    this.selectInFragment(fragment, "templateEmail").value = email;
    this.selectInFragment(fragment, "templateInitials").textContent = email
      .slice(0, 2)
      .toUpperCase();
    this.selectInFragment(fragment, "templateLabel").textContent = label;

    // Member rows lead with the name and carry the email as a second line;
    // raw-email rows have nothing else to show, so the line goes away.
    const subline = this.selectInFragment(fragment, "templateSubline");
    if (label !== email) subline.textContent = email;
    else subline.remove();

    this.listTarget.appendChild(fragment);

    if (userId) this.addedUserIds.add(userId);
    this.addedEmails.add(email);
    this.notifyChanged();
  }

  // Row count incl. read-only existing memberships. Broadcast so the editor's
  // hub row can mirror it, and toggle the mobile empty-state hero.
  notifyChanged() {
    const count = this.listTarget.querySelectorAll(
      ".affiliate-invite-row"
    ).length;

    if (this.hasEmptyTarget) this.emptyTarget.hidden = count > 0;
    this.dispatch("changed", { detail: { count } });
  }

  trackRow(row) {
    const email = row.querySelector("input[name*='[email]']")?.value;
    const userId = row.querySelector("input[name*='[user_id]']")?.value;
    if (email) this.addedEmails.add(email);
    if (userId) this.addedUserIds.add(userId);
  }

  clearSearch() {
    this.searchInputTarget.value = "";
    this.clearResults();
  }

  clearResults() {
    clearTimeout(this.searchTimeout);
    this.resultsTarget.removeAttribute("src");
    this.resultsTarget.innerHTML = "";
  }

  selectInFragment(fragment, name) {
    return fragment.querySelector(`[data-${this.identifier}-target="${name}"]`);
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
}
